/**
 * kit/tokens.ts — единственный источник палитры, типографики, геометрии и
 * движения UI-кита (дизайн-система «Аркадный автомат»,
 * docs/ui/design-system/01-foundations.md §14, 03-motion.md §2). CSS-строка
 * `TOKENS_CSS` выводится программно из тех же констант, что потребляют
 * inline-стили, поэтому расхождение между JS-токенами и CSS-переменными
 * исключено.
 */

/**
 * Primitives (01-foundations §1). Components never use them directly — only
 * through the roles in COLORS, so one shade can change without hunting usages.
 */
export const PALETTE = {
  felt950: '#0A2621', felt900: '#0F362E', felt800: '#154539', felt700: '#1C5A4C', felt600: '#23705E',
  ink950: '#0C1416', ink900: '#1A1F24',
  slate900: '#1B282D', slate800: '#24343A', slate700: '#2E4248', slate600: '#40565E', slate400: '#6F858C',
  cream50: '#FFFBF0', cream100: '#F6F0DE', cream200: '#EDE7D3', cream300: '#D8D0B9',
  cream400: '#D0C9B2', cream500: '#A9A28D', cream700: '#5E584A',
  red200: '#FFC2BD', red300: '#FF7A70', red500: '#F0483E', red700: '#B8281F',
  blue200: '#BFE0FF', blue500: '#2E8DEB', blue700: '#1C64B0',
  gold200: '#FFE7A6', gold400: '#FFC23D', gold700: '#C98A0C',
  orange500: '#FF8A1F', orange700: '#C25E05',
  pink200: '#FFC4DD', pink300: '#FF8DBB', pink500: '#FF4F9A', pink700: '#C2286C',
  ice300: '#8EE3F5', ice600: '#2FA9C7',
  green400: '#5FD27A', green700: '#2E9A4B',
  indigo200: '#C9D0EE', indigo700: '#3A4568', indigo800: '#2C3550', indigo950: '#191E30',
} as const;

/** A primitive at a given opacity, for the few roles that are translucent. */
const alpha = (color: string, percent: number): string =>
  `color-mix(in srgb, ${color} ${percent}%, transparent)`;

/**
 * Colour roles (01-foundations §2). Every key becomes `--c-<kebab-key>`.
 */
export const COLORS = {
  /* Surfaces — §2.1 */
  /** The table: the night arcade hall every screen lies on (01 §8, owner's choice 2026-10-04). */
  tableBg: PALETTE.indigo950,
  /** The table's pixel dot, one every 32px. */
  tableDot: PALETTE.indigo700,
  appBg: PALETTE.indigo950,
  panelBg: PALETTE.slate800,
  /** «Корпус»: the top menu strip, admin and the crash screen (01 §8). */
  housingBg: PALETTE.slate900,
  panelRaised: PALETTE.slate700,
  panelEdge: PALETTE.slate600,
  fieldBg: PALETTE.ink950,
  scrim: alpha(PALETTE.ink950, 72),
  zoneBg: alpha(PALETTE.ink950, 20),
  outline: PALETTE.ink900,
  stepShadow: PALETTE.ink950,
  /** A rule drawn on the table — a section divider (05 §5). Steps on the night
      table are solid ink (01 §7, K2): ink at 40 % vanished into the indigo. */
  tableRule: alpha(PALETTE.ink950, 40),
  /** Step of a card in the air — dragged or flying (01 §7: step-lift at 25 %). */
  stepLift: alpha(PALETTE.ink950, 25),
  /* Cards — §2.2. Look A «Тёмный корпус» (Q14): a dark slate body that belongs
     to the night cabinet, the art in a warm light window framed in ink. */
  cardFace: PALETTE.slate700,
  cardFaceToken: PALETTE.slate600,
  cardFaceSpell: PALETTE.indigo700,
  cardArtWindow: PALETTE.cream200,
  /** A darker gold than the selection ring, so a structure never reads as picked. */
  cardRimStructure: PALETTE.gold700,
  cardRimReaction: PALETTE.ice300,
  cardBack: PALETTE.indigo800,
  cardBackPattern: PALETTE.indigo950,
  cardBackPatternLight: PALETTE.indigo700,
  cardBackMark: PALETTE.gold400,
  /** Text on a card's dark face: name, rules (the face is slate or indigo). */
  textOnCardFace: PALETTE.cream200,
  textMutedOnCardFace: PALETTE.cream500,
  /** A light plate on a dark ground: the cell-effect tag (02 §4.3). */
  plateLight: PALETTE.cream100,
  /* Game values — §2.3 */
  attack: PALETTE.red500,
  health: PALETTE.blue500,
  energy: PALETTE.gold400,
  selection: PALETTE.gold400,
  heat: PALETTE.orange500,
  action: PALETTE.orange500,
  actionStep: PALETTE.orange700,
  dangerStep: PALETTE.red700,
  freeze: PALETTE.ice300,
  freezeEdge: PALETTE.ice600,
  rival: PALETTE.pink500,
  positive: PALETTE.green400,
  negative: PALETTE.red500,
  /* Text — §2.4 */
  textOnDark: PALETTE.cream200,
  textMutedOnDark: PALETTE.cream500,
  textMutedOnTable: PALETTE.cream400,
  textOnCard: PALETTE.ink900,
  textMutedOnCard: PALETTE.cream700,
  textOnSpell: PALETTE.cream200,
  textNegativeOnDark: PALETTE.red300,
  textRivalOnDark: PALETTE.pink300,
  textOnAction: PALETTE.ink900,
  textOnEnergy: PALETTE.ink900,
  textOnColor: PALETTE.cream50,
  textDisabled: PALETTE.slate400,
  /** Brand mark halves (§10): your invader on top, the rival's skull below. */
  logoTop: PALETTE.gold400,
  logoBottom: PALETTE.pink500,
  /** Fill of glyph-less art (§10). */
  glyphFallbackArt: PALETTE.indigo700,
  glyphFallbackArtMark: PALETTE.cream50,
  /** Light painted through a mask: card glare and the flash of a swallowed card (03 §7). */
  glare: PALETTE.cream50,
  /** White: only digits on chips and the core of a combat flash (§1). */
  combatCore: '#FFFFFF',
} as const;

/**
 * Card art size inside a deck glyph strip: a 20×20 authored glyph at ×1, so the
 * pixels stay whole (01 §10). Shared with the surface CSS so rows that host a
 * strip can reserve its height before any deck has loaded.
 */
export const DECK_GLYPH_SIZE = 20;
/** Art window the strip draws around each glyph: `padding` inside a stroke-m rim. */
export const DECK_GLYPH_ITEM_PADDING = 5;
export const DECK_GLYPH_ITEM_BORDER = 2;
/** Outer height of one strip item, and therefore of the strip itself. */
export const DECK_GLYPH_ROW_HEIGHT =
  DECK_GLYPH_SIZE + 2 * (DECK_GLYPH_ITEM_PADDING + DECK_GLYPH_ITEM_BORDER);

/**
 * Font roles (01-foundations §3.1): pixel display for numbers, short buttons
 * and labels; a readable grotesque for names, rules and forms; monospace only
 * for technical surfaces (game id, admin, sandbox editor).
 */
export const FONTS = {
  // Press Start 2P has no U+2212 minus and no arrows: those glyphs fall back to
  // Rubik rather than to a thin system monospace dash.
  display: "'Press Start 2P', 'Pixelify Sans', 'Rubik Variable', 'Rubik', ui-monospace, monospace",
  text: "'Rubik Variable', 'Rubik', ui-sans-serif, system-ui, sans-serif",
  code: "'JetBrains Mono Variable', 'JetBrains Mono', ui-monospace, monospace",
} as const;


/**
 * Type scale (01-foundations §3.2). Press Start 2P is drawn on an 8px grid;
 * 10/12px are for digits and short outlined labels only. Each entry becomes a
 * `font` shorthand `--type-<key>`.
 */
export const TYPE_SCALE = {
  'display-xl': { family: 'display', size: 48, lineHeight: 1.2, weight: 400 },
  'display-l': { family: 'display', size: 32, lineHeight: 1.25, weight: 400 },
  'display-m': { family: 'display', size: 24, lineHeight: 1.3, weight: 400 },
  'display-s': { family: 'display', size: 16, lineHeight: 1.4, weight: 400 },
  'display-xs': { family: 'display', size: 12, lineHeight: 1.4, weight: 400 },
  'display-2xs': { family: 'display', size: 10, lineHeight: 1.4, weight: 400 },
  'display-3xs': { family: 'display', size: 8, lineHeight: 1.5, weight: 400 },
  'text-l': { family: 'text', size: 17, lineHeight: 1.45, weight: 500 },
  'text-m': { family: 'text', size: 15, lineHeight: 1.5, weight: 500 },
  'text-s': { family: 'text', size: 13, lineHeight: 1.4, weight: 500 },
  'text-xs': { family: 'text', size: 11, lineHeight: 1.35, weight: 600 },
  /** Fields on touch screens: below 16px iOS zooms the page on focus. */
  'text-input': { family: 'text', size: 16, lineHeight: 1.5, weight: 500 },
} as const satisfies Record<string, {
  family: keyof typeof FONTS;
  size: number;
  lineHeight: number;
  weight: number;
}>;

/**
 * Pixel icon sizes (01 §9): the icons are drawn on a 7×7 grid, so they are
 * whole only at 1×, 2× and 3×. `--icon-<key>`; an icon sized by font-size
 * (1em) takes one of these.
 */
export const ICON_SIZES = { s: 7, m: 14, l: 21 } as const;

/** 4px grid (01-foundations §4). `--space-<key>`. */
export const SPACE = { 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 8: 32, 12: 48 } as const;
/** Corner radii (§5). `--radius-<key>`. */
export const RADII = { xs: 4, s: 6, m: 8, l: 10, xl: 12, '2xl': 14, round: 999 } as const;
/** Outline widths (§6); objects never get a 1px outline. `--stroke-<key>`. */
export const STROKES = { l: 3, m: 2, s: 1 } as const;
/**
 * Stepped shadow offsets (§7): depth is a solid shadow moved straight down,
 * never blurred. `--step-<key>`; the colour comes from the surface underneath.
 */
export const STEPS = { 0: 0, 1: 2, 2: 3, 3: 5, 4: 7, lift: 12 } as const;

/** Dark pixel outline around light text in 8 directions, without blur. */
const pixelOutline = (px: number): string => [
  [-px, -px], [0, -px], [px, -px], [-px, 0], [px, 0], [-px, px], [0, px], [px, px],
].map(([x, y]) => `${x}px ${y}px 0 ${PALETTE.ink900}`).join(', ');

/**
 * Text outlines (01-foundations §3.4). White digits on coloured chips need
 * them to reach contrast, so the outline is part of the token, not decoration.
 * `outline3` ends with a step taken from `--text-outline-3-step`. A custom
 * property resolves its var() where it is declared, so as a token on :root the
 * step is always ink; a surface that wants a role-coloured step spells the
 * outline out itself (see `.cards-match-result-title`).
 */
export const TEXT_OUTLINES = {
  outline1: pixelOutline(1),
  outline2: `${pixelOutline(2)}, 0 3px 0 ${PALETTE.ink900}`,
  outline3: `${pixelOutline(3)}, 0 6px 0 var(--text-outline-3-step, ${PALETTE.ink900})`,
} as const;

/**
 * Motion curves: the SHAPE of a movement, named by intent, so a wind-up or an
 * arrival reads the same on every surface. How LONG battle beats last stays in
 * TURN_PLAYBACK_TIMING (TurnPlayback.tsx), which playback waits on; these only
 * say how a beat moves inside its window.
 *
 * A curve given to a multi-stop keyframe applies to EVERY segment, so each pose
 * is reached early and then held — a stutter per stop. Multi-stop keyframes run
 * `linear` overall and put one of these on the individual stop instead.
 */
export const MOTION_EASINGS = {
  /** Leaves fast, settles softly: arrivals, recoils, feedback. */
  out: 'cubic-bezier(.2, .85, .3, 1)',
  /** Gathers speed into a moment: the closing half of a flip, a strike, a fall. */
  in: 'cubic-bezier(.55, 0, .85, .35)',
  /** Starts and ends at rest: a pull-back, a lift. */
  inOut: 'cubic-bezier(.45, 0, .55, 1)',
  /** Arrives a touch past the mark and comes back. */
  overshoot: 'cubic-bezier(.34, 1.4, .64, 1)',
} as const;

/**
 * Interface durations, ms (03-motion §2.2) — everything that is not a battle
 * beat. `--dur-<key>`.
 */
export const MOTION_DURATIONS = {
  /** Press of a button or card, sinking onto its step. */
  tap: 80,
  /** Hover, popover exit, modal backdrop and exit. */
  quick: 140,
  /** Popover and modal entry, screen change, toasts. */
  base: 200,
  /** Hand FLIP, landing in a cell, plan orb entry. */
  move: 260,
  /** Number recount (useCountTween default). */
  count: 320,
  /** Match-result and round banners entering. */
  emphasis: 360,
  /** One hand-card sway cycle. */
  sway: 3400,
  /** One loader cycle: three coins hop in turn. */
  loader: 1200,
  /** Two-frame blink of a target sight and of the drop target under a card. */
  blink: 820,
  /** A plan thread catching its target: the flare, the knot and its chips. */
  latch: 420,
  /** One trip of a plan thread's dashes along its length. */
  flow: 1800,
  /** One loop of a card name too long for its strip, scrolling to be read. */
  marquee: 7000,
  /** One loop of the BETA ticker strip along the bottom edge. */
  ticker: 100_000,
  /** How long a skipped turn rings what it changed (03 §5). */
  afterSkip: 1200,
  /** One cycle of the «Готов» nudge when it is time to press it (02 §5.2). */
  nudge: 2400,
  /** A win's confetti falling from the banner (03 §4.14). */
  celebrate: 1200,
} as const;

/** Stepped timing functions (03-motion §2.3) — the pixel frame. `--steps-<key>`. */
export const MOTION_STEPS = {
  flash: 'step-end',
  blink: 'steps(2, jump-none)',
  dissolve: 'steps(4)',
  bounce: 'steps(3)',
  shake: 'step-end',
} as const;

/**
 * Card drag/flight physics (03-motion §2.4): the "heavy card" feel shared by
 * the battle hand, the board and the sandbox passive badge.
 */
export const MOTION_SPRINGS = {
  /** Following the pointer: under-damped so the card lags and overshoots. */
  followStiffness: 190,
  followDamping: 11,
  /** Flying home after a missed drop: softer, braking near the slot. */
  returnStiffness: 80,
  returnDampingFrom: 7,
  returnDampingTo: 14,
  /** Distance from the slot over which the return brakes up to returnDampingTo, px. */
  returnBrakeDistance: 60,
  /** How far a dragged card may trail the pointer, px. */
  maxDragLag: 34,
  /** Hard ceiling of a flight so a stuck spring never strands the ghost, ms. */
  flightDeadlineMs: 1_200,
  /** A flight with no visible destination (a cast spell) parks after this, ms. */
  flightNoTargetMs: 260,
  /** Dissolve of a parked card over its destination, ms. */
  flightLandingFadeMs: 140,
} as const;

/** camelCase → kebab-case (appBg → app-bg, darkCardText → dark-card-text). */
const kebab = (key: string): string => key.replace(/[A-Z]/g, (ch) => `-${ch.toLowerCase()}`);

/**
 * CSS custom properties derived from the tokens above: `--c-<role>` per colour,
 * `--font-<role>`, `--type-<step>` (font shorthand), `--space-*`, `--radius-*`,
 * `--stroke-*`, `--step-*`, `--text-outline-*`, `--ease-*`, `--dur-*` and
 * `--steps-*`.
 */
export const TOKENS_CSS = `:root { ${[
  ...Object.entries(COLORS).map(([key, value]) => `--c-${kebab(key)}: ${value};`),
  ...Object.entries(FONTS).map(([key, value]) => `--font-${key}: ${value};`),
  ...Object.entries(TYPE_SCALE).map(([key, { family, size, lineHeight, weight }]) =>
    `--type-${key}: ${weight} ${size}px/${lineHeight} var(--font-${family});`),
  // The step's size alone, for a dense layout that keeps its own family and
  // leading but must stay on the scale (K3).
  ...Object.entries(TYPE_SCALE).map(([key, { size }]) => `--type-${key}-size: ${size}px;`),
  ...Object.entries(ICON_SIZES).map(([key, value]) => `--icon-${key}: ${value}px;`),
  ...Object.entries(SPACE).map(([key, value]) => `--space-${key}: ${value}px;`),
  ...Object.entries(RADII).map(([key, value]) => `--radius-${key}: ${value}px;`),
  ...Object.entries(STROKES).map(([key, value]) => `--stroke-${key}: ${value}px;`),
  ...Object.entries(STEPS).map(([key, value]) => `--step-${key}: ${value}px;`),
  ...Object.entries(TEXT_OUTLINES).map(([key, value]) => `--text-${kebab(key).replace(/(\d)$/u, '-$1')}: ${value};`),
  ...Object.entries(MOTION_EASINGS).map(([key, value]) => `--ease-${kebab(key)}: ${value};`),
  ...Object.entries(MOTION_DURATIONS).map(([key, value]) => `--dur-${key}: ${value}ms;`),
  ...Object.entries(MOTION_STEPS).map(([key, value]) => `--steps-${key}: ${value};`),
].join(' ')} }`;
